"use client";

import Image from "next/image";
import { useEffect, useMemo, useRef } from "react";

// [RUDDERSTACK-OLD disabled 2026-07-20] widget_viewed → now central widget_impression
// (TrackedWidget in NewPage); see webapp/docs/analytics
// import { FireWidgetViewedEvent } from "@/customAnalytics/trackingEvents";
import { cn, formatSectionTitle } from "@/lib/utils";

interface PausedStyle {
  mainFontSize: number;
  mainOpacity: number;
  descFontSize: number;
  descOpacity: number;
}

interface SalaryItem {
  meta_id?: number;
  meta_salary_package: string;
  meta_package_description?: string;
  meta_priority?: number;
  meta_status?: boolean;
}

interface AcademicDeliverables2Data {
  title?: string;
  title_text_color?: string;
  sub_title?: string;
  sub_title_text_color?: string;
  section_background_color?: string;
  recruiters_title?: string;
  desktop_bg_image?: string;
  mobile_bg_image?: string;
  alt_image?: string;
  metadata?: SalaryItem[];
  status?: boolean;
}

const ITEM_SPACING = 150;

// Self-contained port of the Chandigarh `animatePlacementsTexts` helper: scrolls
// the salary rows upward, scaling font-size/opacity as each row passes through
// the focal band, and pausing briefly when a row hits max size.
function animateSalaryTexts(
  texts: (HTMLElement | null)[],
  positions: number[],
  pausedStyles: React.MutableRefObject<PausedStyle[]>,
  pauseStartTime: React.MutableRefObject<number | null>,
  animateFn: () => void
) {
  if (pauseStartTime.current) {
    const elapsed = Date.now() - pauseStartTime.current;
    if (elapsed < 1000) {
      texts.forEach((text, index) => {
        if (text && pausedStyles.current[index]) {
          const mainText = text.children[0] as HTMLElement;
          const descText = text.children[1] as HTMLElement;

          mainText.style.fontSize = `${pausedStyles.current[index].mainFontSize}px`;
          mainText.style.opacity = pausedStyles.current[index].mainOpacity.toString();

          descText.style.fontSize = `${pausedStyles.current[index].descFontSize}px`;
          descText.style.opacity = pausedStyles.current[index].descOpacity.toString();
        }
      });

      requestAnimationFrame(animateFn);
      return;
    } else {
      pauseStartTime.current = null;
    }
  }

  let anyTextAtMaxSize = false;
  pausedStyles.current = [];

  texts.forEach((text, index) => {
    if (!text || positions[index] === undefined) return;

    positions[index] -= 0.5;
    text.style.top = `${positions[index]}px`;

    const position = positions[index];

    let mainFontSize: number;
    if (position >= 180 && position <= 399) {
      mainFontSize = 52 + ((399 - position) / (399 - 180)) * (64 - 52);
    } else if (position >= 80 && position < 180) {
      mainFontSize = 64 - ((180 - position) / (180 - 80)) * (64 - 54);
    } else if (position > 399) {
      mainFontSize = 52;
    } else {
      mainFontSize = 53;
    }

    let mainOpacity: number;
    if (mainFontSize === 52) {
      mainOpacity = 0.5;
    } else if (mainFontSize === 64) {
      mainOpacity = 1;
    } else if (mainFontSize === 44) {
      mainOpacity = 0.2;
    } else {
      mainOpacity = 0.2 + ((mainFontSize - 44) / (64 - 44)) * (1 - 0.2);
    }

    let descFontSize: number;
    if (position >= 180 && position <= 399) {
      descFontSize = 8 + ((399 - position) / (399 - 180)) * (14 - 8);
    } else if (position >= 80 && position < 180) {
      descFontSize = 14 - ((180 - position) / (180 - 80)) * (14 - 12);
    } else if (position > 399) {
      descFontSize = 8;
    } else {
      descFontSize = 12;
    }

    const descOpacity = mainOpacity;

    pausedStyles.current[index] = {
      mainFontSize,
      mainOpacity,
      descFontSize,
      descOpacity,
    };

    const mainText = text.children[0] as HTMLElement;
    const descText = text.children[1] as HTMLElement;

    mainText.style.fontSize = `${mainFontSize}px`;
    mainText.style.opacity = mainOpacity.toString();

    descText.style.fontSize = `${descFontSize}px`;
    descText.style.opacity = descOpacity.toString();

    if (mainFontSize >= 64) {
      anyTextAtMaxSize = true;
    }
  });

  texts.forEach((_, index) => {
    if (positions[index] < -50) {
      const maxPosition = Math.max(...positions);
      positions[index] = maxPosition + ITEM_SPACING;
    }
  });

  if (anyTextAtMaxSize && !pauseStartTime.current) {
    pauseStartTime.current = Date.now();
  }

  requestAnimationFrame(animateFn);
}

const AcademicDeliverables2 = ({ data }: { data: any }) => {
  const elementRef = useRef<HTMLElement>(null);
  const textRefs = useRef<(HTMLDivElement | null)[]>([]);
  const pauseStartTime = useRef<number | null>(null);
  const pausedStyles = useRef<PausedStyle[]>([]);

  // Handle both single and double nesting from PreviewRenderer
  const sectionData: AcademicDeliverables2Data = data?.data ?? data ?? {};

  const {
    title = "",
    title_text_color = "#FFFFFF",
    sub_title = "",
    sub_title_text_color = "#FFFFFF",
    section_background_color = "#db5803",
    recruiters_title = "",
    desktop_bg_image = "",
    mobile_bg_image = "",
    alt_image = "",
    metadata = [],
  } = sectionData;

  // Active salary rows, sorted by priority (mirrors Scholar's metadata handling).
  const salary = useMemo(() => {
    if (!metadata || metadata.length === 0) return [];
    return [...metadata]
      .filter((item) => item.meta_status)
      .sort((a, b) => (a.meta_priority ?? 0) - (b.meta_priority ?? 0));
  }, [metadata]);

  // [RUDDERSTACK-OLD disabled 2026-07-20] impression now central; see webapp/docs/analytics
  // useEffect(() => {
  //   if (elementRef.current) {
  //     FireWidgetViewedEvent(elementRef, "academic_deliverables_section");
  //   }
  // }, []);

  useEffect(() => {
    if (salary.length === 0) return;

    const texts = textRefs.current;
    const positions = salary.map((_, i) => 400 + i * ITEM_SPACING);
    let frameId = 0;

    function animate() {
      animateSalaryTexts(texts, positions, pausedStyles, pauseStartTime, animate);
    }

    frameId = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(frameId);
  }, [salary.length]);

  if (!sectionData || !sectionData.status || salary.length === 0) {
    return null;
  }

  return (
    <section
      ref={elementRef}
      className="flex flex-col items-center justify-center w-full mx-auto"
    >
      {/*
        Desktop switch is pinned to a literal 1024px via `min-[1024px]:` rather than
        `lg:` — this theme remaps --breakpoint-lg to 1430px, so `lg:` kept the mobile
        (stacked + upscaled mobile image) layout on laptops and at 125% browser zoom,
        where the viewport reports ~1150-1210px. The reference CU build breaks at
        Tailwind's default lg = 1024px.
      */}
      <div
        className="w-full flex items-center justify-center py-10 min-[1024px]:py-0"
        style={{ backgroundColor: section_background_color }}
      >
        <div className="w-full max-w-[1200px] mx-auto flex flex-col min-[1024px]:flex-row items-center justify-center min-[1024px]:justify-between gap-10 min-[1024px]:gap-16 px-7 min-[1024px]:px-12">
          <div className="flex flex-col gap-4 w-full min-[1024px]:w-[420px] min-[1024px]:py-[118px] justify-center">
            {title && (
              <p
                className="text-[28px] text-center min-[1024px]:text-left leading-[126%] min-[1024px]:text-[32px] font-semibold"
                style={{ color: title_text_color }}
              >
                {title}
              </p>
            )}
            {sub_title && (
              <p
                className="text-[14px] text-center min-[1024px]:text-left min-[1024px]:text-[18px] font-normal leading-[22px]"
                style={{ color: sub_title_text_color }}
              >
                {sub_title}
              </p>
            )}
          </div>

          <div className="relative h-[400px] w-full min-[1024px]:w-[360px] overflow-hidden flex items-center justify-center min-[1024px]:justify-end shrink-0">
            {salary.map((item, index) => (
              <div
                key={item.meta_id || `salary-${index}`}
                ref={(el) => {
                  textRefs.current[index] = el;
                }}
                className="absolute top-[400px] w-full text-white whitespace-nowrap flex flex-col gap-3 items-center min-[1024px]:items-start"
              >
                <p className="font-bold leading-none text-center min-[1024px]:text-left">
                  {item.meta_salary_package}
                </p>
                <p className="font-normal leading-none text-center min-[1024px]:text-left">
                  {item.meta_package_description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {recruiters_title && (
        <p
          className="text-[28px] leading-[126%] font-semibold pt-5 pl-10 w-full text-left"
          // Scope the theme's --color-primary to the configured section color so the
          // `{...}` highlight (text-primary) matches the band background, as in the
          // reference. Cascades only to this heading's spans.
          style={{ ["--color-primary" as string]: section_background_color } as React.CSSProperties}
          dangerouslySetInnerHTML={{
            __html: formatSectionTitle(recruiters_title, "text-primary"),
          }}
        />
      )}

      {desktop_bg_image && (
        <div className="w-full">
          <Image
            src={desktop_bg_image}
            width={1114}
            height={280}
            className="hidden min-[1024px]:block w-full"
            alt={alt_image}
          />
          <Image
            src={mobile_bg_image || desktop_bg_image}
            width={768}
            height={368}
            className={cn("min-[1024px]:hidden w-full h-full")}
            alt={alt_image}
          />
        </div>
      )}
    </section>
  );
};

export default AcademicDeliverables2;
